import DefaultIyugaLogo from "@assets/images/iyuga.jpg";
import _ from "lodash";
import PropTypes from "prop-types";
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { flushSync } from "react-dom";
import CreatePieChart from "../D3Wheels/createPie";
// sampleData.js (475 KB of demo segments) was ONLY the `chartData` default prop
// below. Every real <WheelComponent> passes chartData explicitly, so the demo
// dataset never reached users yet sat in the bundle. Replaced with a tiny inline
// fallback (see FALLBACK_CHART_DATA) so the 475 KB leaves the bundle.
// Direct import (NOT the ../PortraitMedia barrel): the barrel also pulls in
// PortraitMediaPanel -> MediaSelection -> heic2any, which would drag the whole
// authoring/upload chain into this core wheel renderer. PortraitVideoOverlay
// only depends on React, so importing it directly keeps WheelComponent's import
// graph unchanged for the flag-OFF path.
import PortraitVideoOverlay from "../PortraitMedia/PortraitVideoOverlay";
import {
  SEGMENT_COMPUTED_LABELS_ENABLED,
  WHEEL_SEGMENTS_FIRST_PAINT_ENABLED,
} from "../../constants/featureFlags";
import {
  withComputedChartLabels,
  restoreOriginalTitles,
} from "../../utils/segmentLabels";
// EW-003 u1b: hydrate each segment's top-level `fill` MODEL (createPie's
// wedgeFillProps reads d.data.fill) so the CLOSED WEDGE renders its fill on every
// user-facing surface (Builder canvas, View Wheel, Gallery) — Drawchart.jsx is the
// only OTHER caller and is NOT mounted on those. Mirrors Drawchart.jsx:337-342.
// Flag OFF → hydrateChartFill returns the SAME array reference → byte-identical.
import { SEGMENT_ARC_FILLS_ENABLED } from "../D3Wheels/segmentArcFillsFlag";
import { hydrateChartFill } from "../SegmentBuilder/Shared/SegmentStyling/logic/fillModelFeed";
import wheelPropsEqual from "./wheelPropsEqual";
// EW-003 GIF-hover (tile preview): on hover (web) / focus (mobile/keyboard) swap
// a gif wedge's frozen poster for an animating <img>. Opt-in per surface via the
// gifHoverPreview prop; flag OFF or prop false => no listeners => byte-identical.
import { TILE_GIF_HOVER_ENABLED } from "../D3Wheels/tileGifHoverFlag";
// ISP-9999 Segment-wedge hover motion: on a large browse tile (the same surfaces
// that opt into gifHoverPreview) hovering the wheel eases every wedge outward in a
// subtle radial "breath". Flag OFF or non-tile surface => createPie never adds the
// class/vars => byte-identical.
import { SEGMENT_HOVER_MOTION_ENABLED } from "../D3Wheels/segmentHoverMotionFlag";
// A11y (ISP-4020): guarantees the wheel-svg :focus-visible ring (and the other
// D3 wheel styles) load wherever this component renders — the ViewWheel uses
// WheelComponent directly (not Drawchart, which is the only other scss importer).
import "../D3Wheels/index.scss";

// ─── PORTRAIT LAYER GEOMETRY (ISP-C4) ───────────────────────────────────────
// The three portrait layers (white backing disc, click target, video overlay)
// are ONE circle: 60% of the wheel's diameter, inset 20% from the container's
// top-left corner.
//
// THE BUG THIS FIXES. They used to be written as `calc(${width} * 0.6)` /
// `calc(${width} * 0.2)` — sized off the `width` PROP, whose default is "40vw".
// But the rendered container is sized by `containerDivStyle` at nearly every
// call site (see memoizedContainerStyle: containerDivStyle spreads OVER width),
// and almost no call site passes `width`. The two geometries therefore diverged
// and the backing disc rendered up to 11x too large — measured live on
// web-stg 2026-07-24: 460.8px inside a 70px avatar at a 1920px viewport, and
// 364.8px inside the Share modal's 72px avatar at 1520px. It is invisible
// wherever it lands on white, and glaring on the Share/Invite modal's dark
// scrim, because `z-index:-1` paints it above the stacking-context root's
// background but below all in-flow content.
//
// THE FIX IS THE CLASS, NOT THE THREE INSTANCES. Percentages resolve against
// the real rendered container, so there is one source of truth for one
// geometry and any call site — present or future, however it sizes its
// container — is correct by construction.
//
// EQUIVALENCE PROOF (why this is a no-op wherever the old code was already
// right). For an absolutely-positioned box, `width`, `left`, AND percentage
// MARGINS (including margin-top) all resolve against the containing block's
// WIDTH — here, this component's container. So wherever the container's used
// width equals the `width` prop W — which is every call site that renders
// correctly today — `60%` === `calc(W * 0.6)` and `20%` === `calc(W * 0.2)`
// exactly, for the size and for BOTH offsets. Same pixels, same subpixels.
//
// WHY NOT `height: 60%` / `top: 20%`. Those percentages resolve against the
// container's HEIGHT, which is a genuinely different length here: `height`
// defaults to "78vh" while `width` defaults to "40vw", and call sites such as
// WheelPantry/children/WheelCard.jsx set ONLY a container height. Using them
// would squash the circle into an ellipse and mis-place it vertically on every
// non-square container — a regression on surfaces that are correct today.
// Hence `aspectRatio` for the height and `top: 0` + `marginTop` for the
// vertical inset. `aspect-ratio` is already load-bearing in this exact feature
// area (WheelGallery/PrimaryWheel's PRIMARY_WHEEL_CONTAINER_STYLE and
// ExploreV2/ExploreWheelCard's WheelArtSquare both rely on it).
// ISP-9999 hairline-thickness-REDRIVE: this fixed 60%/20% box was only ever an
// approximation of createPie's own portraitRadius()-computed hole (exact for
// image portraits, which size directly off it) — right for View Wheel's
// desktop layout (the size/margin combo it was measured against) but wrong by
// a different amount for every other margin/container-size pairing (Discover
// grid cards, Discover hero cards, Gallery grid cards, ...). It is now only the
// FALLBACK used before the real measurement lands (first paint) or when
// createPie is stubbed without a portraitRadius() method (this repo's existing
// WheelComponent/centerMedia.test.js and portraitVideoOverlayGeometry.test.js
// both do this, and stay pinned to this exact fallback on purpose). See
// recomputePortraitBoxPx below for the real, per-instance measurement.
const PORTRAIT_LAYER_GEOMETRY = {
  position: "absolute",
  width: "60%",
  aspectRatio: "1 / 1",
  top: 0,
  left: "20%",
  marginTop: "20%",
  borderRadius: "50%",
};

// Frozen module constants: the geometry no longer depends on any prop, so these
// are referentially stable for free (they replace three per-render useMemos).
const PORTRAIT_BG_STYLE = Object.freeze({
  ...PORTRAIT_LAYER_GEOMETRY,
  backgroundColor: "white",
  zIndex: -1,
  pointerEvents: "none",
});
const PORTRAIT_CLICK_STYLE = Object.freeze({
  ...PORTRAIT_LAYER_GEOMETRY,
  backgroundColor: "transparent",
  zIndex: 1,
  cursor: "pointer",
});
const PORTRAIT_OVERLAY_STYLE = Object.freeze({
  ...PORTRAIT_LAYER_GEOMETRY,
  zIndex: 2,
  pointerEvents: "none",
});

const WheelComponent = forwardRef(function WheelComponent(
  {
    userData,
    chartData,
    onArcSelected,
    setChartData,
    maxSelectedArcs,
    showLabels,
    containerDivStyle,
    arcThickness,
    margin,
    height = "78vh",
    segmentHighlighting,
    width = "40vw",
    resize,
    onClickPortrait,
    // p12-cl4-segment-library-cards-have-no-accessible-name: the chart's own
    // root container (this.parent in createPie.js) otherwise carries no text,
    // title, aria-label, or img alt — the wheel's segments are painted as pure
    // SVG geometry, so a card built from nothing but this component (e.g. the
    // Segment Library's wheel-listing thumbnail) is invisible to a screen
    // reader. Optional and undefined by default so every existing caller that
    // doesn't pass it renders byte-identical.
    ariaLabel,
    // WL-056b (delta-QA 2026-07-13): optional VIDEO center-media overlay for the
    // live v1 wheel. Shape { type:'video', source, poster }. When present (only
    // ViewWheel/MobileView pass it, and only under VIDEO_PORTRAIT_ENABLED) a
    // muted-autoplay-loop video + its ornaments render clipped to the center
    // portrait circle, over the D3 SVG. undefined => nothing new renders =>
    // byte-identical to today. GIF already animates on the D3 <img> path, so it is
    // not routed here (see PortraitVideoOverlay header).
    centerMedia,
    // EW-003 GIF-hover: when true (tile surfaces only), hovering/focusing the wheel
    // animates its gif wedges. Default false => the View-Wheel / builder wheel is
    // untouched (its gifs already animate in the OPEN frame).
    gifHoverPreview = false,
    // ISP-9999 #920hover (collections-hover-grow-REDRIVE-live-frames) — an
    // explicit per-surface escape hatch from the wedge-hover "breath" that
    // `gifHoverPreview` otherwise bundles in below (`enableWedgeMotion`).
    // gifHoverPreview also drives several OTHER "this is a browse tile"
    // behaviors this surface still wants (full-bleed segments, non-interactive,
    // no backing disc, deferred autoplay) — undefined (every existing tile
    // caller) keeps today's gifHoverPreview-derived value untouched; a surface
    // that wants the tile treatment WITHOUT the grow motion (Collections'
    // showcased-wheel cover, which Gordon's #920hover decision bans "grow/
    // motion" from) passes `false` explicitly.
    enableSegmentHoverMotion,
    // ISP-9999 Discover "live ecosystem": an ambient, non-hover trigger for the
    // SAME segment-wedge hover-breath a tile already wires on real hover/focus.
    // Driven by a page-level timer (useDiscoverLiveEcosystem), not the pointer —
    // see the effect below, which calls the identical `setWedgeHoverExpansion`
    // the real hover listener calls (createPie.js applyWedgeHoverMotion). Default
    // false => byte-identical to today on every surface that never passes it.
    ambientActive = false,
    // stgfix-1008-B43 (clicking a segment / closing the panel flashes the wheel
    // small-then-huge with most pills vanishing): see the useLayoutEffect below
    // for the MEASURED mechanism. Default false => byte-identical for every
    // caller except the Builder (SegmentBuilder/index.jsx passes
    // `wheelFitActive`, the one surface that resizes this component's
    // container via a synchronous React prop rather than organic CSS/window
    // layout).
    syncResizeOnPropChange = false,
    config = {
      isNotDraggable: true,
      segmentHighlighting: segmentHighlighting,
      segmentLableOpacity: 1,
      segmentLableLimit: 25,
      inactiveOpacity: 0.2, // Default inactive opacity added to config
      showTooltips: true,
      centerGap: 0, // Controls the space between center image and segments
    },
  },
  ref
) {
  const parentRef = useRef(null);
  const pieInstanceRef = useRef(null);
  const isViewMode = false;
  const [isPortraitHovered, setIsPortraitHovered] = useState(false);
  // BUG #927h: the optional card-mode video overlay (below) mounts UNDER the D3
  // svg this component owns, so its own onMouseEnter/onMouseLeave never wins
  // elementsFromPoint at the pointer. Hold a ref to it so the card-root hover
  // re-dispatch (triggerHoverEnter/Leave below) can drive its hover-play
  // directly instead of relying on DOM hit-testing.
  const portraitOverlayRef = useRef(null);

  // ISP-9999 hairline-thickness-REDRIVE: the three portrait layers' real pixel
  // box, measured from the mounted CreatePieChart instance instead of assumed
  // via PORTRAIT_LAYER_GEOMETRY's fixed 60%/20%. null until the first real
  // measurement lands (mount, or any test that stubs out createPie without a
  // portraitRadius() method) -- callers fall back to the fixed percentages, so
  // this is purely additive. See recomputePortraitBoxPx below for the why.
  const [portraitBoxPx, setPortraitBoxPx] = useState(null);

  // The wedge "hole" createPie leaves for the portrait is portraitRadius() --
  // pieInnerRadius + arcHolePadding() + centerGap (createPie.js:507-511) --
  // centered on this component's own container (createPie.js's `this.parent`
  // IS parentRef.current; its donut is translated to that container's exact
  // center, createPie.js:558-561). PORTRAIT_LAYER_GEOMETRY instead assumes that
  // hole is ALWAYS exactly 60% of the container width, inset 20% -- true only
  // for the one container-size/margin combination it was measured against
  // (View Wheel's desktop layout). Every other margin/container-size pairing
  // (Discover grid card margin:10, Discover hero margin:6, Gallery grid card
  // margin:25, ...) yields a genuinely different real fraction, so a video
  // portrait (routed through this box, via PortraitVideoOverlay) lands the
  // wrong size while an image portrait (sized directly off createPie's own
  // portraitRadius(), createPie.js:952/1091) always fits its hole exactly.
  // Measured live on web-stg (main.a4254783.js) 2026-08-29, Discover /explore,
  // ring-thickness/circle-radius ratio at 16 angles per card: video-portrait
  // cards ("Meet Your Wellness Coach" 0.062, "Meet Your Coach — Personal
  // Training" 0.0625) vs. image-portrait cards ("Lock Test" 0.0087, "The Role
  // and Impact of Demos..." 0.0028) -- video rings ran 7-22x thicker,
  // proportionally, than image rings on the exact same page.
  // builder-white-hub-lags-wheel-relayout (Gordon 2026-09-02, MEASURED live on
  // web-stg /builder/5146: a relayout that changes the wheel's container width
  // -- e.g. the right-rail nav swapping panels -- runs createPie's setSvg()/
  // updateArcs() synchronously, so the SVG wedges and the D3-drawn centre
  // portrait (setProfile's own foreignObject) resize in the SAME paint. This
  // white backing disc (portraitBgStyle below) is a SEPARATE plain React-state-
  // driven <div>, so its resize only lands on React's next commit -- normally
  // imperceptible, but under real render load (the Builder's heavy component
  // tree) that commit measured 44-107ms behind the already-painted SVG, during
  // which the disc sat at its STALE size/position (measured: 448.7px wide at
  // left:121.7px, vs the correct 195.3px at left:248.375px -- off by 126px+).
  // flushSync forces this state update to commit in the SAME tick as the D3
  // mutation that triggered it, closing the gap. Effects only (never render),
  // so flushSync's "no nested flush during render" restriction never applies.
  //
  // hw-0914-dev-segment-select-white-ghost-disc (Gordon 2026-09-14, MEASURED
  // live on web-dev.iyuga.net, main.af840613.js, /builder/4908, headless
  // Chromium DOM poll every 40ms across 8 trials): flushSync closes the
  // REACT-commit-lag gap above, but not a SEPARATE gap -- `node` (the
  // WheelComponent container) can be mid a CSS width/margin TRANSITION when
  // this runs (e.g. the Builder's segment-select side-pane opening/closing
  // reflows the wheel column). The `<svg>` sits in normal flex flow, so the
  // BROWSER re-centers its (JS-fixed-width) box on every transition frame for
  // free, no JS involved -- but the old `left/top: "Xpx"` here was a FROZEN
  // snapshot of node.clientWidth/Height at this one instant, so it stayed put
  // until the next debounced recompute while the SVG kept sliding underneath
  // it. Trial 6/8 caught the disc 240px off-center from the live SVG mid-
  // transition (t~160ms after a segment-select click), settling only once
  // the transition finished and the next recompute landed (t~200ms).
  // `calc(50% - Xpx)` fixes this the same way flushSync fixed the commit-lag
  // case: left/top are now a live expression the browser itself re-resolves
  // against node's CURRENT (possibly still-animating) box on every paint, so
  // the disc tracks the SVG's live centering exactly instead of a stale
  // snapshot. width/height stay plain pixels (unchanged) -- only the
  // centering offset needs to be resize-live, not the fixed portraitRadius()
  // diameter itself.
  // gv-0914c-dev-white-ghost-disc-third-report-narrow-window (Gordon 2026-09-14
  // 23:32 PT, MEASURED live on web-dev.iyuga.net main.d281a281.js, /builder/4908,
  // AFTER #5461 above was already merged+deployed): #5461 fixed left/top going
  // stale mid-transition, but this recompute is only ever INVOKED from
  // updateSize() below, which is 50ms-settle-DEBOUNCED (waits for resize
  // events to stop). A SUSTAINED relayout (the segment-select content-editor
  // pane opening, a react-spring-driven width animation that keeps firing
  // WheelComponent's own ResizeObserver for ~500ms+) keeps resetting that
  // debounce for its entire duration, so this never runs mid-animation --
  // pie.portraitRadius() (see createPie.js, now resize-live for exactly this
  // reason) goes uncalled, and the diameter/half baked into this calc() stays
  // pinned to its PRE-relayout value for the whole animation while the "50%"
  // term tracks the container LIVE. Since half is now WRONG-SIZED relative to
  // the shrinking container, `calc(50% - half)` increasingly overshoots the
  // true center as the container shrinks -- MEASURED: a 206px disc parked
  // ~115px up-left of the (correctly live-centered, already 344px) wheel
  // mid-transition, self-correcting only once the animation settles and the
  // debounced recompute finally lands.
  //
  // Fix (two parts): (1) createPie.js's portraitRadius() no longer trusts the
  // cached pieInnerRadius field -- it re-measures live every call, so calling
  // it late or often is always correct, never stale. (2) the new rAF-throttled
  // ResizeObserver below calls THIS function on nearly every tick of a
  // sustained relayout, decoupled from the heavy updateArcs()/manageLabels()
  // redraw's 50ms settle-debounce, so the disc's size -- not just its
  // centering offset -- is live throughout the animation, not just at rest.
  const recomputePortraitBoxPx = useCallback(() => {
    const pie = pieInstanceRef.current;
    const node = parentRef.current;
    if (!pie || !node || typeof pie.portraitRadius !== "function") return;
    const diameter = pie.portraitRadius() * 2;
    const half = diameter / 2;
    flushSync(() => {
      setPortraitBoxPx({
        width: diameter,
        height: diameter,
        left: `calc(50% - ${half}px)`,
        top: `calc(50% - ${half}px)`,
      });
    });
  }, []);

  // Runs recomputePortraitBoxPx on (rAF-throttled) EVERY tick of a container
  // relayout, not just once the heavy redraw's 50ms debounce settles below.
  // Mirrors SegmentBuilder/index.jsx's own canvasPaneBox observer (rAF-
  // coalesced, not setTimeout-debounced) for exactly this reason. Cheap (one
  // D3 getter call + a React state write) and side-effect-free -- it never
  // mutates the D3 instance or the SVG -- so it is safe to run far more often
  // than the updateArcs()/manageLabels() redraw in updateSize() below.
  const portraitResizeFrameRef = useRef(null);
  const syncResizeOnPropChangeRef = useRef(syncResizeOnPropChange);
  syncResizeOnPropChangeRef.current = syncResizeOnPropChange;
  // Filled in below, once updateSizeInputsRef exists.
  const redrawForCurrentSizeRef = useRef(() => {});
  useEffect(() => {
    if (!resize || !parentRef.current) return undefined;
    const node = parentRef.current;
    const tick = () => {
      portraitResizeFrameRef.current = null;
      // review-1008-Frontend-6763 (T4.16, MEASURED live web-stg main.91574a72.js,
      // builder/507106): closing the Segment panel grows the Builder's wheel
      // box through a 0.2s CSS transition (SegmentBuilder wheelFitTransition).
      // No size PROP changes during that transition, so only the
      // ResizeObservers see it: this rAF tick grew the portrait every frame
      // while the ring waited for updateSize()'s 50ms settle-debounce, so a
      // full-size portrait (483px) sat over the old small ring (264px) for
      // ~200ms. For the Builder (syncResizeOnPropChange) redraw the ring on
      // this SAME tick, exactly like the prop-change layout effect below, so
      // the portrait and the ring always move on one trigger.
      if (syncResizeOnPropChangeRef.current && pieInstanceRef.current) {
        redrawForCurrentSizeRef.current();
        return;
      }
      recomputePortraitBoxPx();
    };
    const observer = new ResizeObserver(() => {
      // T4.17 (MEASURED live web-stg main.9a7616cb.js, builder/507029, seg-0
      // open): the wheel box's 0.2s CSS transition lands its last step
      // (359.8 -> 344px) during the long tasks of the segment panel mount. The
      // svg box follows the container on that layout, but a rAF-deferred
      // redraw paints one frame first with the ring at the OLD centre offset
      // (+8px,+8px) while the HTML video/portrait layers sit at the new
      // centre -- a white crescent lower-right for ~130ms. A ResizeObserver
      // callback runs after layout and BEFORE paint, so for the Builder redraw
      // here, in the same frame the box changed.
      if (syncResizeOnPropChangeRef.current && pieInstanceRef.current) {
        if (portraitResizeFrameRef.current != null) {
          cancelAnimationFrame(portraitResizeFrameRef.current);
          portraitResizeFrameRef.current = null;
        }
        redrawForCurrentSizeRef.current();
        return;
      }
      if (portraitResizeFrameRef.current != null) return;
      portraitResizeFrameRef.current = requestAnimationFrame(tick);
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (portraitResizeFrameRef.current != null) {
        cancelAnimationFrame(portraitResizeFrameRef.current);
        portraitResizeFrameRef.current = null;
      }
    };
  }, [resize, recomputePortraitBoxPx]);

  // Memoize config object to prevent unnecessary re-renders
  const memoizedConfig = useMemo(() => config, [config]);

  // ISP-9999: a large browse tile (gifHoverPreview) gets the segment-wedge hover
  // "breath" when the flag is on. gifHoverPreview is the existing "this is a
  // browse tile" opt-in (View-Wheel / builder / mini-avatar wheels never set it),
  // so this reuses that gate exactly and needs no per-card change. The pie reads
  // config.segmentHoverMotion; effectiveConfig injects it without mutating the
  // caller's config. Flag OFF / non-tile => false => byte-identical.
  const enableWedgeMotion =
    enableSegmentHoverMotion === undefined
      ? gifHoverPreview && SEGMENT_HOVER_MOTION_ENABLED
      : enableSegmentHoverMotion;
  const effectiveConfig = useMemo(
    () => ({ ...memoizedConfig, segmentHoverMotion: enableWedgeMotion }),
    [memoizedConfig, enableWedgeMotion]
  );

  // Memoize userData to prevent unnecessary re-renders.
  // userData?.name is a dep so the portrait's alt text (ISP-4018) updates when
  // the wheel name arrives async, instead of being memo-swallowed.
  // ISP-9999: a card surface that opted into gif-hover (gifHoverPreview + the flag)
  // tags userData so createPie's setProfile renders a gif portrait still-first and
  // wires the hover swap. Non-opt-in surfaces pass userData through untouched
  // (byte-identical); the flag is stable per surface so it never re-memoizes.
  const memoizedUserData = useMemo(
    () =>
      userData && gifHoverPreview && TILE_GIF_HOVER_ENABLED
        ? { ...userData, gifHoverPreview: true }
        : userData,
    [userData?.id, userData?.url, userData?.name, gifHoverPreview]
  );

  // Memoize chartData for stability.
  // DL-029: when SEGMENT_COMPUTED_LABELS_ENABLED, route the data through the ONE
  // shared label helper so unnamed segments render "Segment N" by CURRENT wheel
  // order (renumbers on reorder). Flag OFF returns the original array by
  // reference => byte-identical. Original stored titles are never mutated; the
  // write-back below strips any computed label before it can be persisted.
  // gordon-0920-editwheel-interaction-glitches: this used to depend on
  // `JSON.stringify(chartData)` so the memo would skip recompute when a
  // parent re-render handed down a new array reference with unchanged
  // content. But `chartData` carries every Segment's full craft.js `nodes`
  // JSON (the canvas content), so that stringify walked and serialized the
  // WHOLE wheel's content on every single WheelComponent render — including
  // every Segment-select click, which is exactly the hot path this component
  // sits on. Measured as a real >50ms main-thread long task on an 8-Segment
  // wheel with image+text content, contributing to the reported
  // clunky/stuttery click-to-select feel. The work this memo actually skips
  // (`withComputedChartLabels`, a shallow `.map()`) is far cheaper than the
  // stringify guarding it, and <WheelComponent> is already wrapped in
  // React.memo(wheelPropsEqual) (wheelPropsEqual.js), which does its own
  // targeted per-field comparison and skips re-rendering this component
  // entirely unless `chartData`'s actual content changed — so by the time
  // this line runs, a plain reference dependency is enough.
  const memoizedChartData = useMemo(
    () =>
      SEGMENT_COMPUTED_LABELS_ENABLED
        ? withComputedChartLabels(chartData)
        : chartData,
    [chartData]
  );

  // EW-003 u1b: attach the top-level `fill` MODEL (createPie.wedgeFillProps reads
  // d.data.fill) so the CLOSED WEDGE paints its image/gif/pattern fill on this
  // shared renderer — the ONLY path Builder/View Wheel/Gallery use. Drawchart.jsx
  // does the SAME hydration; it just isn't mounted on any user-facing wheel.
  // Flag OFF → hydrateChartFill returns memoizedChartData by REFERENCE → this memo
  // yields the same array → byte-identical render. Flag ON but a segment carries no
  // fill (legacy) → resolveSegmentFill returns null → that item is returned
  // unchanged → its wedge markup is byte-identical too. Fed to the pie everywhere
  // memoizedChartData used to be (constructor, updateArcs, resize redraw).
  const hydratedChartData = useMemo(
    () => hydrateChartFill(memoizedChartData, SEGMENT_ARC_FILLS_ENABLED),
    [memoizedChartData]
  );

  // Memoize container style
  //
  // ISP-9999 stray-white-disc-under-wheel-tablet-width: `display:flex` +
  // center/center. createPie's <svg id="chart"> (createPie.js:426) is a plain
  // unstyled child sized to `Math.min(clientWidth, clientHeight)` (createPie.js
  // resize(): svgHeightWidth) — default block/inline flow leaves it pinned to
  // this container's TOP-LEFT corner whenever the container itself is NOT
  // square. `width` defaults to "40vw" and `height` to "78vh" (two independent
  // CSS lengths), and the Builder's own BUILDER_LAYOUT_FIT path only forces a
  // square `containerDivStyle` AFTER its own pane ResizeObserver has measured a
  // size (SegmentBuilder/index.jsx wheelFitActive) — before that lands, or
  // whenever the container is otherwise non-square (e.g. a narrow tablet
  // canvas with the Wheel Settings pane open), the container is taller than
  // it is wide. Meanwhile recomputePortraitBoxPx below (the portrait backing
  // disc/click-target/video-overlay box) centers on the FULL
  // `node.clientWidth/clientHeight` on BOTH axes — correct only when the ring
  // and the container share a center, i.e. only when the container is square.
  // MEASURED (Gordon screenshot, /builder/5591 @1020x780, Wheel Settings>Info
  // open): a top-anchored ~270px ring with a ~220px white backing disc
  // centered ~140px BELOW it, overlapping the ring's own bottom edge — exactly
  // the top-anchored-ring-vs-center-anchored-disc delta this produces on a
  // ~690-wide, ~620-tall non-square canvas. Centering the flow content here
  // makes the ring track the SAME center point the disc already assumes, so
  // the two can never disagree again — additive (position:absolute children
  // like the disc are unaffected by a flex ancestor) and a no-op wherever the
  // container is already square (the ring already fills it, so centering
  // changes nothing pixel-wise).
  const memoizedContainerStyle = useMemo(
    () => ({
      width,
      position: "relative",
      zIndex: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      ...containerDivStyle,
      height: containerDivStyle?.height || height,
    }),
    [width, height, containerDivStyle]
  );

  // Memoize setChartData to prevent unnecessary re-renders.
  // DL-029 write-back guard: the renderer is fed computed labels, so when it
  // writes data back (e.g. an internal drag-reorder) restore the ORIGINAL stored
  // titles (by id) before propagating, so a computed "Segment N" placeholder is
  // never persisted. Flag OFF passes data straight through (byte-identical).
  const memoizedSetChartData = useCallback(
    (data) => {
      setChartData(
        SEGMENT_COMPUTED_LABELS_ENABLED
          ? restoreOriginalTitles(data, chartData)
          : data
      );
    },
    [setChartData, chartData]
  );

  // Memoize onArcSelected callback
  const memoizedOnArcSelected = useCallback(
    (data) => {
      if (onArcSelected) {
        onArcSelected(data);
      }
    },
    [onArcSelected]
  );

  // Resize handler: trailing debounce so a live width animation (builder pane
  // shrink) coalesces dozens of ResizeObserver frames into one redraw when the
  // container settles instead of rebuilding every frame.
  const resizeDebounceRef = useRef(null);
  // gordon-0915-selected-segment-effect-prod-parity ("double bounce", part 2 —
  // the structural half PR#5584 explicitly left open): updateSize used to be a
  // useCallback depending on [hydratedChartData, showLabels,
  // recomputePortraitBoxPx]. hydratedChartData is a NEW reference on every
  // single select/deselect click (see memoizedChartData above), which gave
  // updateSize a new identity per click — and the ResizeObserver-managing
  // effect below (deps [resize, updateSize]) disconnects + reconstructs the
  // observer on ANY updateSize identity change. Per spec, a freshly-observe()'d
  // ResizeObserver fires its callback once immediately with the CURRENT size
  // regardless of whether the container actually resized — a spurious extra
  // redraw racing the REAL resize the Edit-Wheel drawer triggers when it opens
  // on select. Reading the latest inputs from a ref instead of closing over
  // them keeps updateSize's identity — and the observer — stable across a
  // pure selection change.
  const updateSizeInputsRef = useRef();
  updateSizeInputsRef.current = { hydratedChartData, showLabels, memoizedUserData, recomputePortraitBoxPx };
  // One synchronous ring + labels + portrait redraw at the container's
  // current size. Shared by the Builder's prop-change layout effect and its
  // per-frame ResizeObserver tick (review-1008-Frontend-6763).
  redrawForCurrentSizeRef.current = () => {
    const pie = pieInstanceRef.current;
    if (!pie) return;
    const {
      hydratedChartData: latestChartData,
      showLabels: latestShowLabels,
      memoizedUserData: latestUserData,
      recomputePortraitBoxPx: latestRecomputePortraitBoxPx,
    } = updateSizeInputsRef.current;
    pie.resizeSvg();
    pie.updateArcs(latestChartData, { animate: false });
    pie.manageLabels(latestShowLabels);
    pie.setProfile(latestUserData);
    latestRecomputePortraitBoxPx?.();
  };
  const updateSize = useCallback(() => {
    if (resizeDebounceRef.current) {
      clearTimeout(resizeDebounceRef.current);
    }
    resizeDebounceRef.current = setTimeout(() => {
      resizeDebounceRef.current = null;
      if (pieInstanceRef.current) {
        const {
          hydratedChartData: latestChartData,
          showLabels: latestShowLabels,
          memoizedUserData: latestUserData,
          recomputePortraitBoxPx: latestRecomputePortraitBoxPx,
        } = updateSizeInputsRef.current;
        // gordon-0915-selected-segment-effect-prod-parity ("double bounce",
        // structural half): setSvg() is a FULL teardown — it wipes and
        // recreates every group (arcs/icons/labels/portrait/hidden drag
        // layer). Calling it on every resize tick is what turned a sustained
        // container relayout (the Segment Settings side panel opening) into
        // ~15-20 full rebuilds, measured live as a big snap + a 300-800ms
        // freeze + a stair-step settle instead of a smooth resize
        // (~/segment-select-0915-amend.txt section 2; regression-anchored by
        // createPie.resizeThrash.test.js). resizeSvg() only updates what a
        // resize actually changes — the radii/arcGenerator/svg dimensions
        // (resize()) and the two transform-bearing wrapper groups' center
        // offset — and leaves the arc/icon/label/portrait DOM nodes in place
        // for updateArcs()/manageLabels()/setProfile() below to update via
        // their existing (already efficient) merge/signature-checked paths.
        pieInstanceRef.current.resizeSvg();
        // animate:false — a pure container resize (gordon-0915-selected-segment-
        // effect-prod-parity, Kelly's "double-bounce"): a resize-triggered redraw
        // must settle in ONE frame, never a `.transition()` that a following
        // resize tick 50ms later can interrupt/restart mid-flight.
        pieInstanceRef.current.updateArcs(latestChartData, { animate: false });
        // ISP-4137: draw the segment labels on the resize path too. For a
        // resize=true consumer (the Edit-Wheel builder, wheelFitActive) the
        // ResizeObserver's INITIAL fire is what paints the wheel on mount —
        // updateArcs drew the arcs but not the labels, so labels only ever
        // appeared because the old `id: uuidv4()` re-render churn re-ran the D3
        // effect's else-branch (which calls manageLabels). With the memo now
        // stable that churn is gone, so the labels must be drawn here or they
        // never render. manageLabels is idempotent (removes + redraws) and, being
        // debounced + fired only on an actual size change, adds no per-frame
        // churn. Labels now also track a genuine resize — previously they did not.
        pieInstanceRef.current.manageLabels(latestShowLabels);
        // setProfile() has its own cheap "same signature" fast path (just
        // resizes the existing portrait foreignObject) — needed here because
        // resizeSvg() (unlike setSvg()) no longer calls it implicitly, and a
        // PURE resize (no chartData change) never runs the other D3-update
        // effect below that also calls setProfile().
        pieInstanceRef.current.setProfile(latestUserData);
        latestRecomputePortraitBoxPx();
      }
    }, 50);
  }, []);

  // Effect to handle resizing using ResizeObserver
  useEffect(() => {
    if (!resize || !parentRef.current) return;
    const observer = new ResizeObserver(updateSize);
    observer.observe(parentRef.current);

    return () => {
      if (resizeDebounceRef.current) {
        clearTimeout(resizeDebounceRef.current);
        resizeDebounceRef.current = null;
      }
      observer.disconnect();
    };
  }, [resize, updateSize]);

  // stgfix-1008-B43 (MEASURED live web-stg, bundle main.73fc971f.js, builder/
  // 507359: clicking a segment or closing the Segment Settings panel flashes
  // the wheel small-then-huge with the Segment 1/2 pills gone for ~400-900ms
  // each time): when `syncResizeOnPropChange` is set (only the Builder, via
  // `wheelFitActive`), this component's own wrapping <div ref={parentRef}>
  // is resized by a SYNCHRONOUS React prop (`width`/`containerDivStyle.height`,
  // driven by SegmentBuilder's own useLayoutEffect on canvasPaneBox) on the
  // SAME commit the user's click causes. The <svg id="chart"> inside it is
  // D3-owned and only redraws via the ResizeObserver above, which is
  // inherently async (fires on a later frame at best) AND 50ms-trailing-
  // debounced (updateSize). For that whole window the svg's own width/height
  // attribute (set from resizeSvg()'s `this.resize()`) already matches the
  // NEW smaller container, but the arc `d` paths and label-group transforms
  // (only touched by updateArcs()/manageLabels(), still queued behind the
  // debounce) still encode the OLD, larger-radius geometry. The svg carries
  // `overflow: visible`, so that stale geometry bleeds straight past the new
  // box's edge, under the just-opened side panel, and any pill whose stale
  // position happens to land past that boundary is invisible — MEASURED:
  // Segment 1/2 gone, Segment 3/4/5 (whose stale positions stayed inside the
  // visible pane) unaffected. Mirrors the SAME fix pattern
  // kelly-1002-P2-segment-open-close-staccato-bounce already applied one
  // layer up (SegmentBuilder's own useLayoutEffect on canvasPaneBox): replace
  // "wait for the async observer" with "re-measure/redraw synchronously the
  // moment the size PROP changes", for the imperative D3 half of the same
  // gap. Scoped to `syncResizeOnPropChange` so every other WheelComponent
  // caller (resize defaults to true almost everywhere — cards, ViewWheel,
  // pickers — none of which resize via a synchronous size PROP) is completely
  // unaffected; on mount `pieInstanceRef.current` is still null (the D3-init
  // effect below hasn't run yet), so this is a no-op until a REAL post-mount
  // size-prop change happens.
  useLayoutEffect(() => {
    if (!syncResizeOnPropChange || !resize || !pieInstanceRef.current) return;
    if (resizeDebounceRef.current) {
      clearTimeout(resizeDebounceRef.current);
      resizeDebounceRef.current = null;
    }
    redrawForCurrentSizeRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncResizeOnPropChange, resize, width, containerDivStyle?.height]);

  // Effect to initialize and update D3 instance
  useEffect(() => {
    if (!parentRef.current) return;

    if (!pieInstanceRef.current) {
      pieInstanceRef.current = new CreatePieChart(
        parentRef.current,
        hydratedChartData,
        memoizedUserData,
        memoizedSetChartData,
        isViewMode,
        memoizedOnArcSelected,
        maxSelectedArcs,
        () => {}, // Empty hover callback
        arcThickness,
        () => {},
        effectiveConfig,
        margin
      );
      // First-paint fix (WHEEL_SEGMENTS_FIRST_PAINT): the constructor builds the
      // SVG + center portrait but does NOT draw the segment arcs (only
      // updateArcs() does). Originally scoped to `!resize` on the assumption
      // that every resize=true consumer's ResizeObserver "initial fire" would
      // draw the arcs on mount instead — true in spec (ResizeObserver always
      // fires once on observe()) but NOT in practice: that fire is inherently
      // asynchronous (never lands in the same paint as mount) and funnels
      // through updateSize's 50ms debounce + a full resizeSvg()/updateArcs()/
      // manageLabels()/setProfile() rebuild. stgfix-1008-B42 (MEASURED live on
      // web-stg.iyuga.net, wheel/507585): Builder's own WheelComponent usage
      // (SegmentBuilder/index.jsx: resize={RESIZE_WEDGE_DRAG_ENABLED ||
      // wheelFitActive}) has `resize` permanently true now that
      // RESIZE_WEDGE_DRAG_ENABLED defaults ON in every deployed env — so the
      // `!resize` guard silently excluded the Edit Wheel / Builder route again,
      // reproducing the exact "segments invisible until a side-pane
      // interaction" flash this flag was built to fix, visible as a plain
      // white disc that jumps to full size once the ResizeObserver eventually
      // redraws it. Dropping `!resize` here is safe: the later ResizeObserver
      // fire (resize=true) just redraws the SAME data — an idempotent no-op
      // repaint, not a double-draw bug (same reasoning already proven for the
      // resize=false path below). Flag OFF => byte-identical to today.
      if (WHEEL_SEGMENTS_FIRST_PAINT_ENABLED) {
        pieInstanceRef.current.updateArcs(hydratedChartData);
        pieInstanceRef.current.manageLabels(showLabels);
      }
      recomputePortraitBoxPx();
    } else {
      // Keep the keyboard-nav config in sync on updates (wheelName arrives async
      // after the wheel data loads) and (re)wire the widget listeners. Both are
      // idempotent + no-ops unless config.keyboardNav is set, so non-opt-in
      // usages stay byte-identical.
      if (memoizedConfig?.keyboardNav !== undefined) {
        pieInstanceRef.current.keyboardNav = memoizedConfig.keyboardNav;
      }
      if (memoizedConfig?.wheelName !== undefined) {
        pieInstanceRef.current.wheelName = memoizedConfig.wheelName;
      }
      // ISP-9999: keep the wedge-hover-motion opt-in live across updates. Idempotent;
      // applyWedgeHoverMotion (run by updateArcs below) re-stamps the container class
      // + per-wedge vars, or clears them when this is false. Byte-identical when false.
      pieInstanceRef.current.segmentHoverMotion = enableWedgeMotion;
      // Adaptive segment icons: keep the live showOnSegment in sync when the
      // wheel's `segment_icon` setting changes (the builder control flip). The
      // pie's segmentIconConfig is otherwise only merged at construction, so
      // without this an existing instance would ignore a mid-session change.
      // Guarded on the key being present, so every non-opt-in usage (no
      // segmentIconConfig in config — the flag-OFF default) is byte-identical.
      // updateArcs() below re-runs updateIcons() and picks up the new value.
      if (memoizedConfig?.segmentIconConfig?.showOnSegment !== undefined) {
        pieInstanceRef.current.segmentIconConfig.showOnSegment =
          memoizedConfig.segmentIconConfig.showOnSegment;
      }
      if (pieInstanceRef.current.keyboardNav) {
        pieInstanceRef.current.setupKeyboardNav();
      }
      pieInstanceRef.current.setMode(isViewMode);
      // Keep geometry knobs live — collection covers pass margin:0 /
      // arcThickness:0; without this an already-mounted pie keeps the
      // constructor defaults and leaves a white rim.
      const nextMargin = margin ?? 50;
      const nextThickness = arcThickness || 0;
      const geometryChanged =
        pieInstanceRef.current.margin !== nextMargin ||
        pieInstanceRef.current.arcThickness !== nextThickness;
      if (geometryChanged) {
        pieInstanceRef.current.margin = nextMargin;
        pieInstanceRef.current.arcThickness = nextThickness;
        pieInstanceRef.current.setSvg();
      }
      pieInstanceRef.current.updateArcs(hydratedChartData);
      pieInstanceRef.current.manageLabels(showLabels);
      pieInstanceRef.current.setProfile(memoizedUserData);
      recomputePortraitBoxPx();
    }
  }, [
    hydratedChartData,
    memoizedUserData,
    showLabels,
    memoizedConfig,
    effectiveConfig,
    enableWedgeMotion,
    maxSelectedArcs,
    recomputePortraitBoxPx,
    arcThickness,
    margin,
    memoizedSetChartData,
    memoizedOnArcSelected,
  ]);

  // Tear down keyboard-nav listeners when the wheel unmounts.
  useEffect(() => {
    return () => {
      if (pieInstanceRef.current && pieInstanceRef.current.destroy) {
        pieInstanceRef.current.destroy();
      }
    };
  }, []);

  // EW-003 GIF-hover (tile preview): wire hover (web) / focus (mobile/keyboard)
  // to mount/unmount the animating gif overlay on the pie. Opt-in via
  // gifHoverPreview + the flag; the pie's mount is itself reduced-motion-safe and
  // a no-op when the wheel has no gif wedge. Flag OFF / prop false => no listeners
  // are attached => byte-identical to today. focusin/focusout cover keyboard and
  // touch-focus; pointerenter/leave cover the mouse.
  useEffect(() => {
    const node = parentRef.current;
    if (!node || !gifHoverPreview || !TILE_GIF_HOVER_ENABLED) return undefined;
    const enter = () => {
      pieInstanceRef.current?.mountGifHoverWedges?.();
      pieInstanceRef.current?.animateGifPortrait?.();
    };
    const leave = () => {
      pieInstanceRef.current?.unmountGifHoverWedges?.();
      pieInstanceRef.current?.freezeGifPortrait?.();
    };
    node.addEventListener("pointerenter", enter);
    node.addEventListener("pointerleave", leave);
    node.addEventListener("focusin", enter);
    node.addEventListener("focusout", leave);
    return () => {
      node.removeEventListener("pointerenter", enter);
      node.removeEventListener("pointerleave", leave);
      node.removeEventListener("focusin", enter);
      node.removeEventListener("focusout", leave);
      pieInstanceRef.current?.unmountGifHoverWedges?.();
      pieInstanceRef.current?.freezeGifPortrait?.();
    };
  }, [gifHoverPreview]);

  // ISP-9999 hover-anywhere-on-card: a card shell (ExploreWheelCard, Gallery's
  // WheelCard) is much bigger than this component's own root box, so a real mouse
  // hover over the card's title/byline/chrome never crosses this element and the
  // pointerenter/mouseenter listeners above (and createPie's own
  // "mouseenter.wedgeHover") never fire. Expose a small imperative API so the CARD
  // ROOT can re-fire the SAME native events on this component's root node from its
  // own onMouseEnter/onMouseLeave — every existing gate (gifHoverPreview,
  // TILE_GIF_HOVER_ENABLED, SEGMENT_HOVER_MOTION_ENABLED, prefers-reduced-motion)
  // still lives in the listeners themselves, so this is a pure re-dispatch, not a
  // parallel code path.
  useImperativeHandle(
    ref,
    () => ({
      triggerHoverEnter: () => {
        const node = parentRef.current;
        if (node) {
          node.dispatchEvent(new Event("mouseenter"));
          node.dispatchEvent(new Event("pointerenter"));
        }
        // BUG #927h: reach the (possibly D3-occluded) video overlay directly —
        // a no-op when no card-mode video is mounted.
        portraitOverlayRef.current?.triggerHoverEnter?.();
      },
      triggerHoverLeave: () => {
        const node = parentRef.current;
        if (node) {
          node.dispatchEvent(new Event("mouseleave"));
          node.dispatchEvent(new Event("pointerleave"));
        }
        portraitOverlayRef.current?.triggerHoverLeave?.();
      },
    }),
    []
  );

  // ISP-9999 Discover "live ecosystem": ambient trigger for the wedge-hover
  // "breath". Calls the EXACT SAME instance method createPie's own
  // mouseenter.wedgeHover/mouseleave.wedgeHover listeners call (see
  // applyWedgeHoverMotion in createPie.js) — no duplicate animation code, and
  // no synthetic DOM events to simulate a hover that never happened. Gated on
  // `enableWedgeMotion` (the existing gifHoverPreview + SEGMENT_HOVER_MOTION_ENABLED
  // / enableSegmentHoverMotion opt-in) so a surface that never gets hover motion
  // never gets ambient motion either, and `setWedgeHoverExpansion` itself still
  // honours prefers-reduced-motion as a second, independent guard.
  useEffect(() => {
    if (!enableWedgeMotion) return undefined;
    pieInstanceRef.current?.setWedgeHoverExpansion?.(!!ambientActive);
  }, [ambientActive, enableWedgeMotion]);

  // Memoize portrait click handler
  const memoizedPortraitClickHandler = useCallback(() => {
    if (onClickPortrait) {
      onClickPortrait();
    }
  }, [onClickPortrait]);

  // Shared box for all three portrait layers. Prefers the REAL measured circle
  // (portraitBoxPx, from recomputePortraitBoxPx above) once available; falls
  // back to PORTRAIT_LAYER_GEOMETRY's fixed 60%/20% only before the first
  // measurement lands or when createPie is stubbed without portraitRadius()
  // (e.g. WheelComponent/centerMedia.test.js, portraitVideoOverlayGeometry.test.js
  // — both stay byte-identical to their pinned pre-fix assertions).
  const portraitLayerBoxStyle = useMemo(
    () =>
      portraitBoxPx
        ? {
            position: "absolute",
            width: `${portraitBoxPx.width}px`,
            height: `${portraitBoxPx.height}px`,
            // Already a `calc(50% - Xpx)` expression (see
            // recomputePortraitBoxPx above) -- not a bare number, so no `px`
            // suffix here.
            top: portraitBoxPx.top,
            left: portraitBoxPx.left,
            borderRadius: "50%",
          }
        : PORTRAIT_LAYER_GEOMETRY,
    [portraitBoxPx]
  );

  // White background behind the D3 portrait image.
  const portraitBgStyle = useMemo(
    () => ({
      ...portraitLayerBoxStyle,
      backgroundColor: "white",
      zIndex: -1,
      pointerEvents: "none",
    }),
    [portraitLayerBoxStyle]
  );

  // Transparent click target above the D3 portrait image
  const portraitClickStyle = useMemo(
    () => ({
      ...portraitLayerBoxStyle,
      backgroundColor: "transparent",
      zIndex: 1,
      cursor: "pointer",
    }),
    [portraitLayerBoxStyle]
  );

  // WL-056b: positioning box for the optional video-portrait overlay — the SAME
  // circle geometry the existing portrait bg/click layers use, so the video is
  // clipped exactly to the D3 center portrait. Sits above the D3 svg;
  // pointer-events:none so only the overlay's play/pause button is interactive
  // and every other click still reaches the wheel beneath.
  const showCenterVideo =
    !!centerMedia && centerMedia.type === "video" && !!centerMedia.source;
  const portraitOverlayStyle = useMemo(
    () => ({ ...portraitLayerBoxStyle, zIndex: 2, pointerEvents: "none" }),
    [portraitLayerBoxStyle]
  );

  // Custom tooltip style
  const tooltipStyle = useMemo(
    () => ({
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      backgroundColor: "#333",
      color: "#fff",
      padding: "6px 12px",
      borderRadius: "4px",
      fontSize: "12px",
      fontFamily: "DM Sans, sans-serif",
      whiteSpace: "nowrap",
      pointerEvents: "none",
      zIndex: 10,
    }),
    []
  );

  // Browse/tile surfaces (gifHoverPreview) paint over a wallpaper or a round
  // cover clip — createPie already composites the centre portrait without the
  // old ISP-4018 white wash (RP-A1.2), and brand-logo fallbacks carry their own
  // white field inside the SVG portrait wrap. The HTML backing disc here only
  // reads as a glaring white circle on those cards; View Wheel / Builder /
  // Share surfaces (gifHoverPreview unset) keep it for dark scrims + legacy parity.
  const showPortraitBackingDisc = !gifHoverPreview;

  return (
    <div
      ref={parentRef}
      style={memoizedContainerStyle}
      {...(ariaLabel ? { role: "img", "aria-label": ariaLabel } : {})}
    >
      {showPortraitBackingDisc && <div style={portraitBgStyle} />}
      {showCenterVideo && (
        <div style={portraitOverlayStyle}>
          {/* ISP-C4: the overlay BOX is now container-derived, but
              PortraitVideoOverlay still scales its ornaments (button, caption)
              off the `width` prop — `calc(width * 0.6)`. That stays consistent
              because both surfaces that pass `centerMedia` (pages/ViewWheel
              desktop and its MobileView delegate) pass an explicit width AND
              height and NO containerDivStyle, so the container's used width IS
              this prop. A future centerMedia call site that sizes its container
              instead would need PortraitVideoOverlay's ornament scale moved to
              percentages too (its `chipFont` uses the value in a font-size, so
              a percentage there is not a drop-in — hence out of scope here). */}
          <PortraitVideoOverlay
            ref={portraitOverlayRef}
            source={centerMedia.source}
            poster={centerMedia.poster}
            width={width}
            // BUG #927a/#927c: `gifHoverPreview` is the existing "this is a
            // browse/tile card surface" signal (Discover/Gallery — see the
            // EW-003 GIF-hover comment above); View Wheel/MobileView/the
            // Builder never set it. Reuse it here so a card portrait gets NO
            // center play/pause box, NO "Muted · Loops" badge (interactive
            // false), and a delayed autoplay start instead of an instant one
            // (deferAutoplay true) — while View Wheel keeps its full,
            // instant-autoplay controls unchanged.
            interactive={!gifHoverPreview}
            deferAutoplay={gifHoverPreview}
          />
        </div>
      )}
      {onClickPortrait && (
        <div
          onClick={memoizedPortraitClickHandler}
          onMouseEnter={() => setIsPortraitHovered(true)}
          onMouseLeave={() => setIsPortraitHovered(false)}
          style={portraitClickStyle}
        >
          {isPortraitHovered && (
            <div style={tooltipStyle}>Wheel Portrait</div>
          )}
        </div>
      )}
    </div>
  );
});

WheelComponent.propTypes = {
  userData: PropTypes.shape({
    id: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    name: PropTypes.string, // optional: portrait alt text (ISP-4018)
  }),
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      selected: PropTypes.bool,
      pattern: PropTypes.string,
      title: PropTypes.string,
      icon: PropTypes.elementType,
    })
  ).isRequired,
  onArcSelected: PropTypes.func,
  setChartData: PropTypes.func.isRequired,
  maxSelectedArcs: PropTypes.number,
  showLabels: PropTypes.bool,
  containerDivStyle: PropTypes.object,
  arcThickness: PropTypes.number,
  margin: PropTypes.number,
  segmentHighlighting: PropTypes.bool,
  resize: PropTypes.bool,
  // WL-056b optional video center-media overlay (live v1 wheel).
  centerMedia: PropTypes.shape({
    type: PropTypes.string,
    source: PropTypes.string,
    poster: PropTypes.string,
  }),
  // EW-003 GIF-hover opt-in (tile surfaces).
  gifHoverPreview: PropTypes.bool,
  // ISP-9999 #920hover: explicit wedge-hover-motion override, see the prop's
  // own destructuring comment above. undefined (unset) is the meaningful
  // default -- do not default this in defaultProps, or every caller loses the
  // ability to distinguish "no opinion" from "explicitly false".
  enableSegmentHoverMotion: PropTypes.bool,
  // ISP-9999 Discover live-ecosystem ambient trigger.
  ambientActive: PropTypes.bool,
  // stgfix-1008-B43: opt-in synchronous resize+redraw when `width`/
  // `containerDivStyle.height` change, for the one caller (Builder) that
  // resizes this component's container via a synchronous prop instead of
  // organic CSS/window layout. See the useLayoutEffect's own header.
  syncResizeOnPropChange: PropTypes.bool,
  // p12-cl4-segment-library-cards-have-no-accessible-name: optional accessible
  // name for the chart's root container. undefined (unset) is the meaningful
  // default -- do not default this in defaultProps, or every existing caller
  // would gain a role="img" it never asked for.
  ariaLabel: PropTypes.string,
  // config.centerGap: number (optional) - controls the space between center image and segments
};

// Minimal, valid-shaped fallback used only if a caller renders <WheelComponent>
// without chartData (no such call exists in the app today — every usage passes it).
const FALLBACK_CHART_DATA = [
  { id: 1, color: "#123051", inner_color: "#8BA6C2", activity_tags: [], value: 1 },
];

WheelComponent.defaultProps = {
  chartData: FALLBACK_CHART_DATA,
  userData: {
    id: "1",
    url: DefaultIyugaLogo,
  },
  onArcSelected: () => {},
  maxSelectedArcs: 1,
  showLabels: false,
  containerDivStyle: {},
  arcThickness: -10,
  margin: 50,
  segmentHighlighting: false,
  resize: true,
  gifHoverPreview: false,
  ambientActive: false,
  syncResizeOnPropChange: false,
  config: {
    isNotDraggable: true,
    segmentHighlighting: false,
    segmentLableOpacity: 1,
    segmentLableLimit: 25,
    inactiveOpacity: 0.2,
    showTooltips: true,
    centerGap: 0, // Default gap
  },
};

// Use a custom comparator for the memo to better control rerenders.
// Extracted to ./wheelPropsEqual (testable in isolation) + fixed to also detect a
// Segment Fill MODEL change so the wedge re-renders its fill live in the Builder
// (EW-003, flag REACT_APP_SEGMENT_ARC_FILLS), AND (ISP-9999) to compare segment
// content scalars (title / photo / wallpaper / pattern) so a Builder content edit
// re-renders the pie and cannot be reverted by a later stale-array emit. See that
// file's header for both bugs.
//
// Re-exported here under the ISP-9999 name (+ the compare-key list) so PR #4525's
// call sites and unit test keep resolving them from ./index after the EW-003
// extraction moved the comparator into its own file. `wheelComponentPropsAreEqual`
// is the SAME function as `wheelPropsEqual` — one source of truth, no duplicate.
export {
  wheelComponentPropsAreEqual,
  WHEEL_CHART_ITEM_COMPARE_KEYS,
} from "./wheelPropsEqual";

// ISP-9999 (stgfix-1007-P0-view-wheel-never-hide-empty-ring): React.memo()
// returns a NEW wrapper object and does NOT copy the inner component's
// `defaultProps` onto it. Every caller creates a `<WheelComponent>` element of
// the MEMO WRAPPER's type, so React's defaultProps resolution looked at the
// wrapper (no `defaultProps` of its own) and silently applied NONE of the
// defaults above for any prop a caller omits — including `resize: true`.
// MEASURED (web-stg, bundle main.e615452d.js, wheel 507415, 0 segments):
// ViewWheel/index.jsx never passes `resize` to <ViewWheelRender>/<WheelComponent>
// (unlike SegmentBuilder/index.jsx, which always passes an explicit boolean),
// so View Wheel's `resize` prop resolved to `undefined` instead of `true` --
// not `false` (a deliberate, supported value with its own code path), just
// silently missing. `undefined` is falsy, so WheelComponent/index.jsx's
// ResizeObserver-setup effects (`if (!resize || !parentRef.current) return;`,
// both the arcs-redraw observer and the portrait-box observer) never attached
// for the real wheel container, so NEITHER the ResizeObserver's "fires once on
// observe()" initial draw NOR any later resize ever called updateArcs() --
// the one function that falls back to createPie's blank `emptyChartData` ring
// when chartData is empty (createPie.js updateArcs()). The ring was never
// drawn; the center portrait (drawn unconditionally in the constructor) was
// the only thing left on screen. Builder passes resize explicitly (always a
// real boolean), so it was never affected -- this bug is specific to every
// `<WheelComponent>`/`<ViewWheelRender>` caller that relies on an omitted
// prop's documented default.
const MemoizedWheelComponent = React.memo(WheelComponent, wheelPropsEqual);
MemoizedWheelComponent.defaultProps = WheelComponent.defaultProps;
export default MemoizedWheelComponent;
